import type { Log } from "@sentry/react";
import { describeErrorForLog } from "./sentry-error";

const REDACTED = "[redacted]";
const CIRCULAR = "[circular]";
const TOO_DEEP = "[max depth]";

const MAX_DEPTH = 6;

/**
 * Mirrors the credential lists in `server/src/middleware/logging.redaction.ts`.
 * Stored normalized (lowercased, separators stripped) so `x-access-token`,
 * `accessToken` and `usr_token` all match the same entry.
 */
const CREDENTIAL_KEYS = new Set([
  "accesstoken",
  "apikey",
  "authorization",
  "cookie",
  "otp",
  "password",
  "pin",
  "preauthtoken",
  "refreshtoken",
  "secret",
  "setcookie",
  "token",
  "usrtoken",
  "xaccesstoken",
  "xposaccesstoken",
]);

/**
 * A JWT is a credential whichever key it arrived under — serialized errors nest
 * one in shapes no key list can anticipate.
 */
const JWT = /\beyJ[\w-]{2,}\.[\w-]{2,}\.[\w-]{2,}/g;

function isCredentialKey(key: string): boolean {
  return CREDENTIAL_KEYS.has(key.toLowerCase().replace(/[-_]/g, ""));
}

function isPlainObject(value: object): boolean {
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function redactValue(
  value: unknown,
  depth: number,
  seen: WeakSet<object>,
): unknown {
  if (typeof value === "string") return value.replace(JWT, REDACTED);
  if (typeof value !== "object" || value === null) return value;
  if (value instanceof Error) {
    return redactValue(describeErrorForLog(value), depth, seen);
  }
  if (seen.has(value)) return CIRCULAR;
  if (depth >= MAX_DEPTH) return TOO_DEEP;

  seen.add(value);

  if (Array.isArray(value)) {
    return value.map((entry) => redactValue(entry, depth + 1, seen));
  }

  // Host objects — the XMLHttpRequest hanging off an axios error, a DOM node —
  // hold nothing worth logging and can throw from their own getters.
  if (!isPlainObject(value)) return Object.prototype.toString.call(value);

  return Object.fromEntries(
    Object.entries(value).map(([key, entry]) => [
      key,
      isCredentialKey(key) ? REDACTED : redactValue(entry, depth + 1, seen),
    ]),
  );
}

/**
 * Strips credentials out of every log before it leaves the browser.
 *
 * Call sites are expected to log described errors rather than raw ones, but a
 * single missed call site ships a session JWT to Sentry — `AxiosError.toJSON()`
 * serializes the request config, `x-access-token` header included (DEV-9694).
 * This is the backstop that does not depend on remembering.
 */
export function redactLog(log: Log): Log {
  const message = log.message.replace(JWT, REDACTED);

  return {
    ...log,
    // `replace` returns a bare string, dropping the template metadata Sentry
    // groups parameterized messages by — so keep the original when unchanged.
    ...(message === log.message ? {} : { message: message as Log["message"] }),
    ...(log.attributes
      ? {
          attributes: redactValue(log.attributes, 0, new WeakSet()) as Record<
            string,
            unknown
          >,
        }
      : {}),
  };
}
